import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material'

import React, { useState } from 'react'
import { Emc } from 'src/Libs/flow/installation/forms'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { scanForSmartMeterGatewayInfo } from 'src/Libs/flow/installation/api'
import { logError } from 'src/Libs/flow/utils'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { z } from 'zod'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { EmcNetworkScanButton } from 'src/Libs/flow/advanced-settings/components/Dialogs'
import { Alert } from '@e3dc-react/shell/Elements/Alert'
import { useDialogActions } from 'src/Libs/flow/advanced-settings/components/Dialogs/hooks'
import { QrPairingEditor, QrScanButton } from 'src/Libs/installations'
import { usePortalQuery } from 'src/Libs/api'

// #region Dialog

export interface SmartMeterGatewayPairingDialogProps {
  installationId: number | null
  emc: Emc | null
  currentSki: string
  submitPairingId: (id: string) => void
  onClose?: () => void
}

/**
 * Dialog to pair a smart meter gateway by entering its SKI pairing ID, scanning a QR code, or scanning the local network.
 */
export const SmartMeterGatewayPairingDialog: React.FC<SmartMeterGatewayPairingDialogProps> = ({
  installationId,
  emc,
  currentSki,
  submitPairingId,
  onClose,
}) => {
  const { t } = useCustomTranslation()

  const [pairingId, setPairingId] = useState(currentSki)
  const [isTouched, setIsTouched] = useState(false)
  const updatePairingId = (newId: string): void => {
    setPairingId(newId)
    setIsTouched(true)
  }

  const isValid = getIsValidSki(pairingId)

  const error = isTouched && !isValid ? t('installation.smartMeterGateway.not-allowed-pairing-id') : undefined

  const isSame = currentSki === pairingId

  const handleSubmit = (): void => {
    onClose?.()

    /** Dashes are allow during typing, but must be removed on submition. */
    submitPairingId(pairingId.replaceAll('-', ''))
  }

  const [startNetworkScanner, setStartNetworkScanner] = useState(false)
  const [startQrScanner, setStartQrScanner] = useState(false)
  const isEmcActive = emc?.deviceStatus === 'ok'

  const handleOnClose = (): void => {
    onClose?.()
  }

  const dialogActions = useDialogActions({
    primary: {
      name: t('installation.productEditor.pairingEditor.pairAndSave'),
      onClick: handleSubmit,
      disabled: !!error || isSame,
    },
    secondary: { onClick: handleOnClose },
  })

  return (
    <Dialog
      maxWidth="md"
      onClose={handleOnClose}
      title={t('installation.productEditor.pairingEditor.title.smart-meter-gateway')}
      actions={dialogActions}
    >
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, py: 2 }}>
        <TextField
          name="Smart meter gateway ski pairing id"
          label={t('productEditor.pairingEditor.pairingID.smgw')}
          fullWidth
          value={pairingId}
          onChange={e => updatePairingId(e.target.value)}
          error={!!error}
          helperText={error}
        />
        <EmcNetworkScanButton
          installationId={installationId}
          emc={emc}
          buttonLabel={t('installation.productEditor.scanLocalNetwork')}
          onClick={() => setStartNetworkScanner(!startNetworkScanner)}
          disabled={!isEmcActive}
        />

        <QrScanButton onClick={() => setStartQrScanner(!startQrScanner)} />
      </Box>

      <QrPairingEditor startQrScanner={startQrScanner} setStartQrScanner={setStartQrScanner} setPairingId={setPairingId} />

      {startNetworkScanner && (
        <SmartMeterGatewayPairingEditor installationId={installationId} setPairingId={setPairingId} currentPairingId={pairingId} />
      )}
    </Dialog>
  )
}

//#region Pairing Editor

interface SmartMeterGatewayPairingEditorProps {
  installationId: number | null
  setPairingId: (pairingId: string) => void
  currentPairingId: string | undefined
}

const SmartMeterGatewayPairingEditor: React.FC<SmartMeterGatewayPairingEditorProps> = ({ installationId, setPairingId, currentPairingId }) => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()

  const { data, isPending, error } = usePortalQuery({
    queryKey: ['installation', installationId, 'scan-for-smart-meter-gateway'],
    queryFn: () => scanForSmartMeterGatewayInfo(installationId!, authFetch).then(res => res.value),
    enabled: !!installationId,
    staleTime: 0,
  })

  if (error) {
    return <Typography color="error">{error.message}</Typography>
  }

  if (isPending || !data) {
    return (
      <Box textAlign={'center'}>
        <CircularProgress />
      </Box>
    )
  }

  if (typeof data !== 'object') {
    logError('Smart meter gateway network scan data is not of type object.')
    return null
  }

  if (!data.length) {
    return <Alert severity="error" text={t('installation.productEditor.scanNetwork.noResults')} />
  }

  return (
    <Box maxHeight={500}>
      {data.map(smartMeterGateway => {
        return (
          <NetworkScanResult
            key={smartMeterGateway.ski}
            name={smartMeterGateway.shipId}
            pairingId={smartMeterGateway.ski}
            handleSelect={() => setPairingId(smartMeterGateway.ski)}
            isSelected={currentPairingId === smartMeterGateway.ski}
          />
        )
      })}
    </Box>
  )
}

//#region Scan Result

interface NetworkScanResultProps {
  name: string
  pairingId: string
  handleSelect: () => void
  isSelected: boolean
}

const NetworkScanResult: React.FC<NetworkScanResultProps> = ({ name, pairingId, handleSelect, isSelected }) => {
  const { t } = useCustomTranslation()

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        borderBottom: theme => `1px solid ${theme.palette.grey[500]}`,
        py: 2,
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'normal', sm: 'center' },
      }}
    >
      <Box>
        <Box display={'flex'}>
          <Typography sx={{ lineHeight: 1.5, minWidth: { xs: 60, sm: 100 } }}>{t('common.name')}</Typography>
          <PairingIdText>{name}</PairingIdText>
        </Box>
        <Box display={'flex'}>
          <Typography sx={{ lineHeight: 1.5, minWidth: { xs: 60, sm: 100 } }}>{t(`installation.productEditor.scanNetwork.mac`)}</Typography>
          <PairingIdText>{pairingId}</PairingIdText>
        </Box>
      </Box>
      <Button color={'secondary'} variant={'outlined'} size="large" onClick={handleSelect} disabled={isSelected}>
        {isSelected ? t('installation.productEditor.scanNetwork.selected') : t('installation.productEditor.scanNetwork.select')}
      </Button>
    </Box>
  )
}

const PairingIdText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      fontWeight: 'bold',
      lineHeight: 1.5,
      maxWidth: { xs: '80%', sm: undefined },
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}
  >
    {children}
  </Typography>
)

//#region Validation

const skiSchema = z
  .string()
  .transform(value => value.replaceAll('-', ''))
  .pipe(z.string().regex(/^[a-fA-F0-9]{40}$/))

const getIsValidSki = (pairingId: string): boolean => {
  return skiSchema.safeParse(pairingId).success
}
